// Tenant tarafı — Destek (helpdesk) sayfası
// Müşteri kendi ticket'larını açar, listeler, mesaj atar, kapatır.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const STATUS_LABEL = {
  open:     'Açık',
  pending:  'Yanıt Bekleniyor',
  resolved: 'Çözüldü',
  closed:   'Kapalı',
};
const STATUS_COLOR = {
  open:     { bg: 'rgba(59,130,246,0.12)',  clr: '#2563eb' },
  pending:  { bg: 'rgba(245,158,11,0.12)',  clr: '#d97706' },
  resolved: { bg: 'rgba(16,185,129,0.12)',  clr: '#059669' },
  closed:   { bg: 'rgba(100,116,139,0.12)', clr: '#64748b' },
};
const PRIORITY_LABEL = { low: 'Düşük', normal: 'Normal', high: 'Yüksek', urgent: 'Acil' };
const PRIORITY_COLOR = {
  low:    '#64748b',
  normal: '#2563eb',
  high:   '#d97706',
  urgent: '#dc2626',
};
const CATEGORY_LABEL = {
  general: 'Genel',
  bug:     'Hata Bildirimi',
  feature: 'Yeni Özellik',
  billing: 'Fatura / Ödeme',
  account: 'Hesap / Kullanıcı',
  other:   'Diğer',
};

const fmtDateTime = (s) => s ? new Date(s).toLocaleString('tr-TR') : '—';

export default function SupportPage() {
  const [list, setList]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const r = await api.get('/support/tickets');
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    await fetchList();
    setLoading(false);
  }, [fetchList]);

  useEffect(() => { load(); }, [load]);

  // Liste polling — detay açıksa atlanır
  useEffect(() => {
    const tick = () => { if (!document.hidden && !selectedId) fetchList(); };
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [fetchList, selectedId]);

  if (selectedId) {
    return <TicketDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Destek Talepleri"
        unit="ticket"
        count={list.length}
        actions={<Button size="sm" onClick={() => setCreateOpen(true)}>+ Yeni Destek Talebi</Button>}
        onRefresh={load} refreshing={loading}
      />
      <div className="stocks-table-wrap">
        {loading ? <div style={{ padding: 40, textAlign:'center' }}>Yükleniyor...</div>
         : list.length === 0 ? <EmptyState title="Henüz destek talebiniz yok"
                                            description="Bir sorun yaşıyorsanız 'Yeni Destek Talebi' butonuyla bize iletin." />
         : (
          <table className="stocks-table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Talep No</th>
                <th>Konu</th>
                <th style={{ width: 130 }}>Kategori</th>
                <th style={{ width: 90 }}>Öncelik</th>
                <th style={{ width: 130 }}>Durum</th>
                <th style={{ width: 160 }}>Son Güncelleme</th>
                <th style={{ width: 70, textAlign:'center' }}>Mesaj</th>
              </tr>
            </thead>
            <tbody>
              {list.map(t => {
                const s = STATUS_COLOR[t.status] || STATUS_COLOR.open;
                const unread = !!t.unread_for_customer;
                return (
                  <tr key={t.id} className="stocks-table-row" style={{ cursor: 'pointer',
                       background: unread ? 'rgba(59,130,246,0.06)' : undefined }}
                      onClick={() => setSelectedId(t.id)}>
                    <td>
                      {unread && <span title="Yeni mesaj" style={{ display:'inline-block', width: 8, height: 8, borderRadius: '50%', background:'#2563eb', marginRight: 6 }} />}
                      <span className="stock-code">{t.ticket_number}</span>
                    </td>
                    <td><strong>{t.subject}</strong></td>
                    <td style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>{CATEGORY_LABEL[t.category] || t.category}</td>
                    <td>
                      <span style={{ fontSize:'0.75rem', fontWeight: 700, color: PRIORITY_COLOR[t.priority] }}>
                        ● {PRIORITY_LABEL[t.priority] || t.priority}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize:'0.72rem', padding:'3px 9px', borderRadius: 100, fontWeight: 600, background: s.bg, color: s.clr }}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                    </td>
                    <td style={{ fontSize:'0.78rem', color:'var(--text-tertiary)' }}>{fmtDateTime(t.updated_at || t.created_at)}</td>
                    <td style={{ textAlign:'center', fontWeight: 600 }}>{t.message_count || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {createOpen && <CreateTicketModal onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); setSelectedId(id); }} />}
    </div>
  );
}

/* ── YENİ TİCKET MODALI ─────────────────────────────────────────── */
function CreateTicketModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ subject: '', body: '', priority: 'normal', category: 'general' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!form.subject.trim()) return setErr('Konu zorunlu');
    if (!form.body.trim()) return setErr('Açıklama zorunlu');
    setBusy(true);
    try {
      const r = await api.post('/support/tickets', form);
      if (r.data.success) onCreated(r.data.data.id);
      else setErr(r.data.message || 'Oluşturulamadı');
    } catch (e) {
      setErr(e?.response?.data?.message || 'Hata');
    }
    setBusy(false);
  };

  return (
    <Modal title="Yeni Destek Talebi" onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12, maxWidth: 600 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="Kategori" required>
            <select className="ui-input" value={form.category} onChange={e => f('category', e.target.value)}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormField>
          <FormField label="Öncelik" required>
            <select className="ui-input" value={form.priority} onChange={e => f('priority', e.target.value)}>
              {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Konu" required>
          <input className="ui-input" value={form.subject} onChange={e => f('subject', e.target.value)}
                 placeholder="Kısa, açıklayıcı konu" maxLength={200} autoFocus />
        </FormField>
        <FormField label="Açıklama" required>
          <textarea className="ui-input" rows={6} value={form.body} onChange={e => f('body', e.target.value)}
                    placeholder="Sorununuzu detaylı açıklayın. Hata mesajı, adımlar, ekran görüntüsü vs." />
        </FormField>
        {err && <div style={{ color:'var(--danger)', fontSize: 13 }}>{err}</div>}
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 4 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Gönder'}</Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── TİCKET DETAY ───────────────────────────────────────────────── */
function TicketDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const prevMsgCount = React.useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/support/tickets/' + id);
      if (r.data.success) {
        const newCount = (r.data.data.messages || []).length;
        // İlk yüklemede toast yok; sonraki polling'lerde yeni mesaj geldiyse bildir
        if (prevMsgCount.current !== null && newCount > prevMsgCount.current) {
          const last = r.data.data.messages[newCount - 1];
          if (last?.sender_type === 'staff') {
            setToast(`💬 Destek'ten yeni mesaj: ${last.sender_name || 'Destek'}`);
            setTimeout(() => setToast(null), 5000);
          }
        }
        prevMsgCount.current = newCount;
        setData(r.data.data);
      }
    } catch (e) { console.error(e); }
  }, [id]);

  const initialLoad = useCallback(async () => {
    setLoading(true);
    await fetchData();
    setLoading(false);
  }, [fetchData]);

  useEffect(() => { initialLoad(); }, [initialLoad]);

  // Polling: sayfa görünür durumdaysa 8 saniyede bir sessiz refresh
  useEffect(() => {
    const tick = () => { if (!document.hidden) fetchData(); };
    const iv = setInterval(tick, 8000);
    const onVis = () => { if (!document.hidden) fetchData(); }; // tab geri görünür olunca anında yenile
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchData]);

  const send = async () => {
    if (!reply.trim()) return;
    setErr(null); setSending(true);
    try {
      await api.post(`/support/tickets/${id}/reply`, { body: reply });
      setReply('');
      await fetchData();
    } catch (e) { setErr(e?.response?.data?.message || 'Gönderilemedi'); }
    setSending(false);
  };

  const closeTicket = async () => {
    if (!window.confirm('Bu talep çözüldü olarak kapatılacak. Emin misiniz?')) return;
    try {
      await api.post(`/support/tickets/${id}/close`);
      await fetchData();
    } catch (e) { alert(e?.response?.data?.message || 'Hata'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign:'center', color:'var(--text-tertiary)' }}>Yükleniyor...</div>;
  if (!data) return (
    <div style={{ padding: 24 }}>
      <Button size="sm" variant="ghost" onClick={onBack}>← Geri</Button>
      <div style={{ marginTop: 12, color: 'var(--text-tertiary)' }}>Ticket bulunamadı.</div>
    </div>
  );

  const t = data.ticket;
  const s = STATUS_COLOR[t.status] || STATUS_COLOR.open;
  const isClosed = t.status === 'closed';

  return (
    <div style={{ padding: 0 }}>
      {toast && (
        <div style={{ position:'fixed', top: 70, right: 20, zIndex: 9999,
                      padding:'12px 18px', background:'var(--success, #059669)', color:'#fff',
                      borderRadius: 8, boxShadow:'0 4px 16px rgba(0,0,0,0.18)', fontSize: 14,
                      fontWeight: 500, animation: 'slideIn 0.2s' }}>
          {toast}
        </div>
      )}
      <div style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border-secondary)', padding:'18px 24px' }}>
        <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 8, fontSize:'0.82rem' }}>
          <span style={{ cursor:'pointer', color:'var(--accent-primary)' }} onClick={onBack}>← Destek</span>
          <span style={{ color: 'var(--text-tertiary)' }}>/</span>
          <span style={{ fontWeight: 600 }}>{t.ticket_number}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap: 12 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ margin: 0, fontSize:'1.25rem', fontWeight: 700, letterSpacing:'-0.02em' }}>{t.subject}</h1>
              <span style={{ fontSize:'0.72rem', padding:'3px 10px', borderRadius: 100, fontWeight: 700, background: s.bg, color: s.clr }}>
                {STATUS_LABEL[t.status]}
              </span>
            </div>
            <div style={{ display:'flex', gap: 16, fontSize:'0.8rem', color:'var(--text-secondary)', flexWrap:'wrap' }}>
              <span>📂 {CATEGORY_LABEL[t.category] || t.category}</span>
              <span style={{ color: PRIORITY_COLOR[t.priority] }}>● {PRIORITY_LABEL[t.priority]}</span>
              <span>Açılış: {fmtDateTime(t.created_at)}</span>
            </div>
          </div>
          {!isClosed && <Button variant="ghost" onClick={closeTicket}>✓ Çözüldü Olarak Kapat</Button>}
        </div>
      </div>

      <div style={{ padding: 18, maxWidth: 900, margin: '0 auto' }}>
        {/* İlk açıklama */}
        <MessageBubble sender_type="customer" sender_name={t.contact_name || 'Siz'} body={t.body} created_at={t.created_at} />

        {/* Mesajlar */}
        {data.messages.map(m => (
          <MessageBubble key={m.id} {...m} />
        ))}

        {/* Yanıt formu */}
        {isClosed ? (
          <div style={{ marginTop: 24, padding: 16, background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.2)',
                        borderRadius: 8, color: 'var(--text-tertiary)', textAlign:'center', fontSize:'0.85rem' }}>
            🔒 Bu talep kapalı. Yeni bir konu için <strong>+ Yeni Destek Talebi</strong> oluşturun.
          </div>
        ) : (
          <div style={{ marginTop: 24, padding: 16, background:'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>💬 Yanıt Ekle</div>
            <textarea className="ui-input" rows={4} value={reply} onChange={e => setReply(e.target.value)}
                      placeholder="Mesajınızı yazın..." style={{ width:'100%' }} />
            {err && <div style={{ color:'var(--danger)', fontSize: 13, marginTop: 6 }}>{err}</div>}
            <div style={{ marginTop: 8, textAlign:'right' }}>
              <Button variant="primary" onClick={send} disabled={sending || !reply.trim()}>
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ sender_type, sender_name, body, created_at }) {
  const isCustomer = sender_type === 'customer';
  return (
    <div style={{ marginBottom: 16, display: 'flex', justifyContent: isCustomer ? 'flex-end' : 'flex-start' }}>
      <div style={{ maxWidth: '78%' }}>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4, textAlign: isCustomer ? 'right' : 'left' }}>
          <strong>{isCustomer ? (sender_name || 'Siz') : (sender_name || 'Destek')}</strong>
          {' · '}{fmtDateTime(created_at)}
        </div>
        <div style={{
          padding: '10px 14px',
          background: isCustomer ? 'rgba(59,130,246,0.10)' : 'var(--bg-secondary)',
          border: '1px solid ' + (isCustomer ? 'rgba(59,130,246,0.25)' : 'var(--border-secondary)'),
          borderRadius: 8,
          fontSize: 14,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.5,
        }}>
          {body}
        </div>
      </div>
    </div>
  );
}
